import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../auth/AuthContext'

export default function AccountDeletionPage() {
  const { user } = useAuth()
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!reason.trim()) {
      setError('삭제 사유를 입력해주세요.')
      return
    }

    try {
      setSubmitting(true)
      setError('')
      const userEmail = user?.email || '알 수 없음'
      const title = `[계정 삭제 요청] ${userEmail}`
      
      await api.post('/inquiries', {
        title: title,
        content: reason.trim(),
        category: 'ACCOUNT_RELATED'
      })
      
      setSuccess(true)
    } catch (e) {
      console.error(e)
      setError('계정 삭제 요청 등록에 실패했습니다. 다시 시도해주세요.')
    } finally {
      setSubmitting(false)
    }
  }

  if (success) {
    return (
      <section className="max-w-3xl mx-auto space-y-6">
        <div className="rounded border border-emerald-200 bg-emerald-50 p-6">
          <h2 className="text-xl font-bold text-emerald-800 mb-2">계정 삭제 요청이 접수되었습니다</h2>
          <p className="text-emerald-700 mb-4">
            요청 확인 후 30일 이내에 계정 및 관련 데이터가 삭제됩니다.
            처리 완료 시 이메일로 안내드립니다.
          </p>
          <div className="flex gap-2">
            <Link to="/support/inquiries" className="rounded bg-emerald-600 text-white px-4 py-2 text-sm hover:bg-emerald-700">
              문의 내역 확인
            </Link>
            <Link to="/" className="rounded border border-emerald-300 text-emerald-700 px-4 py-2 text-sm hover:bg-emerald-100">
              대시보드로 이동
            </Link>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">계정 삭제 요청</h1>
        <p className="text-slate-600 mt-2 text-sm">
          설계EZ 앱에서 계정 및 관련 데이터를 삭제하려면 아래 폼을 작성해주세요.
        </p>
      </div>

      {/* 안내 섹션 */}
      <div className="rounded border border-slate-200 bg-slate-50 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-slate-800">안내 사항</h2>
        
        <div>
          <h3 className="font-medium text-slate-700 mb-2">삭제되는 데이터</h3>
          <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
            <li>계정 정보 (이메일, 이름, 전화번호)</li>
            <li>업로드한 파일</li>
            <li>설계 의뢰 내역</li>
            <li>공지 수신 내역</li>
            <li>소셜 로그인 연동 정보</li>
          </ul>
        </div>

        <div>
          <h3 className="font-medium text-slate-700 mb-2">보관되는 데이터</h3>
          <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
            <li>법적 의무에 따른 감사 로그 (법령에 따라 보관)</li>
            <li>거래 내역 (세법에 따라 보관)</li>
          </ul>
        </div>

        <div className="rounded bg-amber-50 border border-amber-200 p-4">
          <h3 className="font-medium text-amber-800 mb-1">처리 기간</h3>
          <p className="text-sm text-amber-700">
            요청 확인 후 <strong>30일 이내</strong>에 계정 및 관련 데이터가 삭제됩니다.
            처리 완료 시 이메일로 안내드립니다.
          </p>
        </div>
      </div>

      {/* 계정 삭제 요청 폼 */}
      <div className="rounded border p-6 space-y-4">
        <h2 className="text-lg font-semibold">계정 삭제 요청</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              계정 정보
            </label>
            <div className="rounded border bg-slate-50 px-3 py-2 text-sm text-slate-600">
              {user?.email || '알 수 없음'} ({user?.name || '알 수 없음'})
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="reason" className="block text-sm font-medium text-slate-700">
              삭제 사유 <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reason"
              className="w-full rounded border px-3 py-2 min-h-[120px] text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="계정 삭제를 요청하는 사유를 입력해주세요."
              required
            />
            <p className="text-xs text-slate-500">
              계정 삭제 요청은 되돌릴 수 없습니다. 신중히 결정해주세요.
            </p>
          </div>

          {error && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              className="rounded bg-red-600 text-white px-6 py-2 text-sm font-medium hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={submitting}
            >
              {submitting ? '요청 중...' : '계정 삭제 요청'}
            </button>
            <Link
              to="/support/inquiries"
              className="rounded border border-slate-300 text-slate-700 px-6 py-2 text-sm hover:bg-slate-50"
            >
              취소
            </Link>
          </div>
        </form>
      </div>

      {/* 추가 안내 */}
      <div className="rounded border border-blue-200 bg-blue-50 p-4">
        <p className="text-sm text-blue-800">
          <strong>참고:</strong> 계정 삭제 요청 후에는 로그인할 수 없으며, 모든 데이터가 삭제됩니다.
          계정 삭제 전에 필요한 데이터를 백업해주세요.
        </p>
      </div>
    </section>
  )
}
